import { useState } from 'react'
import './SGPACalculator.css'
import CGPADisplay from './CGPADisplay'
import TargetCalculator from './TargetCalculator'
import logoImage from '../assets/logo.png'

function SGPACalculator() {
  const [currentCGPA, setCurrentCGPA] = useState('')
  const [completedSemesters, setCompletedSemesters] = useState('')
  const [totalSemesters, setTotalSemesters] = useState('')
  const [targetCGPA, setTargetCGPA] = useState('')

  // Hard-coded credits for each semester (adjust according to your institute)
  const SEMESTER_CREDITS = {
    1: 24,  // Semester 1
    2: 23,  // Semester 2
    3: 22,  // Semester 3
    4: 22,  // Semester 4
    5: 21,  // Semester 5
    6: 23,  // Semester 6
    7: 25,  // Semester 7
    8: 16   // Semester 8
  }

  // Calculate required SGPA for remaining semesters
  const calculateRequiredSGPA = () => {
    const current = parseFloat(currentCGPA)
    const completed = parseInt(completedSemesters)
    const total = parseInt(totalSemesters)
    const target = parseFloat(targetCGPA)
    
    if (!current || !completed || !total || !target || completed >= total) return 0
    
    // Calculate total credits for completed semesters
    let completedTotalCredits = 0
    for (let i = 1; i <= completed; i++) {
      completedTotalCredits += SEMESTER_CREDITS[i] || 0
    }
    
    // Calculate total credits for remaining semesters
    let remainingTotalCredits = 0
    for (let i = completed + 1; i <= total; i++) {
      remainingTotalCredits += SEMESTER_CREDITS[i] || 0
    }
    
    // Current grade points
    const currentGradePoints = current * completedTotalCredits
    
    // Calculate total credits for all semesters
    let totalCredits = 0
    for (let i = 1; i <= total; i++) {
      totalCredits += SEMESTER_CREDITS[i] || 0
    }
    
    // Required total grade points for target CGPA
    const requiredTotalGradePoints = target * totalCredits
    
    // Required grade points for remaining semesters
    const requiredRemainingGradePoints = requiredTotalGradePoints - currentGradePoints
    
    // Required SGPA for remaining semesters
    return remainingTotalCredits > 0 ? (requiredRemainingGradePoints / remainingTotalCredits) : 0
  }

  const requiredSGPA = calculateRequiredSGPA()
  const remainingSemesters = totalSemesters && completedSemesters ? parseInt(totalSemesters) - parseInt(completedSemesters) : 0

  // Get credits breakdown for display
  const getCreditsBreakdown = () => {
    const completed = parseInt(completedSemesters) || 0
    const total = parseInt(totalSemesters) || 8
    
    let completedCredits = 0
    let remainingCredits = 0
    
    for (let i = 1; i <= completed; i++) {
      completedCredits += SEMESTER_CREDITS[i] || 0
    }
    
    for (let i = completed + 1; i <= total; i++) {
      remainingCredits += SEMESTER_CREDITS[i] || 0
    }
    
    return { completedCredits, remainingCredits, totalCredits: completedCredits + remainingCredits }
  }

  return (
    <div className="sgpa-calculator">
      <div className="calculator-container">
        <div className="input-section">
          <h2>Enter Your Details</h2>
          
          <div className="input-group">
            <label htmlFor="current-cgpa">Current CGPA</label>
            <input
              id="current-cgpa"
              type="number"
              placeholder="e.g., 8.5"
              min="0"
              max="10"
              step="0.01"
              value={currentCGPA}
              onChange={(e) => {
                console.log('Current CGPA changed:', e.target.value)
                setCurrentCGPA(e.target.value)
              }}
              style={{ position: 'relative', zIndex: 10 }}
            />
          </div>

          <div className="input-group">
            <label htmlFor="completed-semesters">Completed Semesters</label>
            <input
              id="completed-semesters"
              type="number"
              placeholder="e.g., 4"
              min="1"
              max="8"
              value={completedSemesters}
              onChange={(e) => {
                console.log('Completed semesters changed:', e.target.value)
                setCompletedSemesters(e.target.value)
              }}
              style={{ position: 'relative', zIndex: 10 }}
            />
          </div>

          <div className="input-group">
            <label htmlFor="total-semesters">Total Semesters</label>
            <input
              id="total-semesters"
              type="number"
              placeholder="e.g., 8"
              min="1"
              max="8"
              value={totalSemesters}
              onChange={(e) => {
                console.log('Total semesters changed:', e.target.value)
                setTotalSemesters(e.target.value)
              }}
              style={{ position: 'relative', zIndex: 10 }}
            />
          </div>

          <div className="input-group">
            <label htmlFor="target-cgpa">Target CGPA</label>
            <input
              id="target-cgpa"
              type="number"
              placeholder="e.g., 9.0"
              min="0"
              max="10"
              step="0.01"
              value={targetCGPA}
              onChange={(e) => {
                console.log('Target CGPA changed:', e.target.value)
                setTargetCGPA(e.target.value)
              }}
              style={{ position: 'relative', zIndex: 10 }}
            />
          </div>

          <div className="credits-info">
            <h4>📋 Credit Structure (ECM Course):</h4>
            <div className="credits-grid">
              {Object.entries(SEMESTER_CREDITS).map(([sem, credits]) => (
                <div key={sem} className="credit-item">
                  <span>Sem {sem}:</span>
                  <span>{credits} credits</span>
                </div>
              ))}
            </div>
            <div className="total-credits">
              <strong>Total Credits: {Object.values(SEMESTER_CREDITS).reduce((sum, credits) => sum + credits, 0)}</strong>
            </div>
            <div className="logo-section">
                <img src={logoImage} alt="ECM Logo" className="logo-image" />
                <div className="social-links">
                  <a href="https://linkedin.com/in/anoop-kumar-burnwal-aa0b10318/" target="_blank" rel="noopener noreferrer" className="social-link">
                    <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a href="https://github.com/Anoopkr1906" target="_blank" rel="noopener noreferrer" className="social-link">
                    <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                </div>
            </div>
          </div>

          
        </div>

        <div className="results-section">
          <CGPADisplay currentCGPA={parseFloat(currentCGPA) || 0} />
          
          <TargetCalculator
            targetCGPA={targetCGPA}
            remainingSemesters={remainingSemesters}
            requiredSGPA={requiredSGPA}
            completedSemesters={completedSemesters}
            totalSemesters={totalSemesters}
            creditsBreakdown={getCreditsBreakdown()}
            semesterCredits={SEMESTER_CREDITS}
          />
        </div>
      </div>
    </div>
  )
}

export default SGPACalculator
